import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Inbox, Mail, Copy, Archive, ArrowRight, Loader2, Clipboard,
  Clock, X, Plus, AtSign, Briefcase, Scale, Hash,
} from "lucide-react";

export default function InboxPage() {
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("unread");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pasteOpen, setPasteOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/inbox/me"),
        api.get(`/inbox?status=${status}&limit=100`),
      ]);
      setMe(a.data);
      setItems(b.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const open = async (id) => {
    const r = await api.get(`/inbox/${id}`);
    setActive(r.data);
    setItems((xs) => xs.map((x) => x.id === id ? { ...x, status: "read" } : x));
  };

  const archive = async (id) => {
    await api.post(`/inbox/${id}/archive`);
    setItems((xs) => xs.filter((x) => x.id !== id));
    if (active?.id === id) setActive(null);
    toast.success("Archived");
  };

  const toMatter = async () => {
    if (!active) return;
    try {
      const r = await api.post(`/inbox/${active.id}/to-matter`, {});
      toast.success("Matter + case created");
      navigate(`/app/cases/${r.data.case_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
  };

  const copyAddress = () => {
    if (!me?.address) return;
    navigator.clipboard.writeText(me.address);
    toast.success("Inbox address copied");
  };

  return (
    <div data-testid="inbox-page">
      <PageHeader
        eyebrow="La Boîte"
        title="Legal Inbox"
        description="Forward contracts, notices and legal emails to Marianne. We'll extract dates, parties, and the kind of case — then turn it into a Matter in one tap."
        action={
          <Button
            onClick={() => setPasteOpen(true)}
            variant="outline"
            className="rounded-none border-[#E8E3F0] uppercase tracking-widest text-xs"
            data-testid="inbox-paste-btn"
          >
            <Clipboard size={14} className="mr-2" /> Paste email
          </Button>
        }
      />

      {/* Forwarding address card */}
      {me && (
        <div className="border border-[#E8E3F0] bg-white p-5 mb-6 flex flex-wrap items-center gap-4" data-testid="inbox-address-card">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 border border-[#C5A059] flex items-center justify-center">
              <AtSign size={16} className="text-[#C5A059]" />
            </div>
            <div className="min-w-0">
              <div className="label-eyebrow mb-1">Your inbox address</div>
              <div className="font-mono text-sm break-all" data-testid="inbox-address-value">{me.address}</div>
            </div>
          </div>
          <div className="flex items-center gap-6 text-xs text-[#4A5568]">
            <div><div className="text-[#2A0F5A] font-serif text-xl leading-none">{me.counts.unread}</div>Unread</div>
            <div><div className="text-[#2A0F5A] font-serif text-xl leading-none">{me.counts.total}</div>Total</div>
          </div>
          <button
            onClick={copyAddress}
            className="px-3 py-2 border border-[#E8E3F0] text-[10px] tracking-[0.18em] uppercase hover:border-[#2A0F5A]"
            data-testid="copy-address-btn"
          >
            <Copy size={12} className="inline mr-1" /> Copy
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {["unread", "read", "archived", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border ${status === s ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0]"}`}
            data-testid={`inbox-filter-${s}`}
          >{s}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* List */}
        <div className="md:col-span-1 border border-[#E8E3F0] bg-white max-h-[70vh] flex flex-col">
          <div className="px-5 py-3 border-b border-[#E8E3F0] label-eyebrow flex items-center justify-between">
            <span>{items.length} messages</span>
            {loading && <Loader2 size={12} className="animate-spin" />}
          </div>
          <ul className="divide-y divide-[#E8E3F0] overflow-y-auto flex-1">
            {items.length === 0 && !loading && (
              <li className="px-5 py-10 text-center text-xs text-[#4A5568]">
                No messages. Try the <b>Paste email</b> button to try it instantly.
              </li>
            )}
            {items.map((m) => (
              <li key={m.id}>
                <button
                  onClick={() => open(m.id)}
                  className={`w-full text-left px-5 py-3 hover:bg-[#FAFAF8] ${active?.id === m.id ? "bg-[#FAFAF8]" : ""} ${m.status === "unread" ? "border-l-2 border-[#C5A059]" : ""}`}
                  data-testid={`inbox-msg-${m.id}`}
                >
                  <div className="label-eyebrow text-[10px] mb-1 flex items-center justify-between">
                    <span>{m.from || "—"}</span>
                    <span className="text-[#4A5568]">{m.received_at ? new Date(m.received_at).toLocaleDateString() : ""}</span>
                  </div>
                  <div className="font-serif text-base leading-tight line-clamp-1">{m.subject || "(no subject)"}</div>
                  <div className="text-xs text-[#4A5568] mt-1 line-clamp-2">{m.text}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Detail */}
        <div className="md:col-span-2 border border-[#E8E3F0] bg-white p-6 min-h-[60vh]">
          {!active ? (
            <EmptyState icon={Inbox} title="Pick a message" description="Open any item to see the extracted dates, parties, suggested matter and one-click conversion." />
          ) : (
            <article data-testid="inbox-detail">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="label-eyebrow mb-1">{active.from}</div>
                  <h2 className="font-serif text-2xl leading-tight">{active.subject}</h2>
                  <div className="text-xs text-[#4A5568] mt-1">{active.received_at ? new Date(active.received_at).toLocaleString() : ""}</div>
                </div>
                <button onClick={() => archive(active.id)} className="text-[#4A5568] hover:text-[#8a2121]" data-testid="archive-btn"><Archive size={14} /></button>
              </div>

              {/* Extracted insights */}
              {active.extracted && (
                <div className="border border-[#E8E3F0] bg-[#FAFAF8] p-4 mb-5 grid sm:grid-cols-2 gap-4 text-xs">
                  <ExtractField icon={Scale} label="Suggested case type" value={active.extracted.suggested_case_type} />
                  <ExtractField icon={Briefcase} label="Suggested matter" value={active.extracted.suggested_matter_title} />
                  <ExtractField icon={Clock} label="Dates found" value={(active.extracted.dates || []).join(", ") || "—"} />
                  <ExtractField icon={Hash} label="Amounts" value={(active.extracted.amounts || []).join(", ") || "—"} />
                </div>
              )}

              <div className="whitespace-pre-wrap text-[14px] leading-[1.75] max-h-[40vh] overflow-y-auto text-[#2A0F5A]/90">{active.text}</div>

              <div className="mt-6 pt-5 border-t border-[#E8E3F0] flex flex-wrap items-center gap-3">
                {active.matter_id ? (
                  <div className="text-xs text-[#215a3b] flex items-center gap-1.5">
                    ✓ Already linked to a matter
                    {active.case_id && (
                      <button onClick={() => navigate(`/app/cases/${active.case_id}`)} className="underline hover:text-[#2A0F5A] ml-1">
                        open case
                      </button>
                    )}
                  </div>
                ) : (
                  <Button
                    onClick={toMatter}
                    className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
                    data-testid="to-matter-btn"
                  >
                    Create matter + case <ArrowRight size={14} className="ml-2" />
                  </Button>
                )}
              </div>
            </article>
          )}
        </div>
      </div>

      {pasteOpen && <PasteModal onClose={() => setPasteOpen(false)} onCreated={(m) => { setPasteOpen(false); load(); open(m.id); }} />}
    </div>
  );
}

function ExtractField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={12} className="mt-0.5 text-[#C5A059] shrink-0" />
      <div className="min-w-0">
        <div className="label-eyebrow text-[9px] mb-0.5">{label}</div>
        <div className="text-[#2A0F5A] text-xs">{value || "—"}</div>
      </div>
    </div>
  );
}

function PasteModal({ onClose, onCreated }) {
  const [subject, setSubject] = useState("");
  const [from_, setFrom] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) return toast.error("Paste the email body first");
    setBusy(true);
    try {
      const r = await api.post("/inbox/paste", { subject, from: from_, text });
      toast.success("Added to inbox");
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-[#0B132B]/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="paste-modal">
      <div className="bg-white max-w-2xl w-full mt-16 border border-[#E8E3F0]">
        <div className="px-6 py-4 border-b border-[#E8E3F0] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">La Boîte</div>
            <h2 className="font-serif text-2xl">Paste an email</h2>
          </div>
          <button onClick={onClose} className="text-[#4A5568] hover:text-[#2A0F5A]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label className="label-eyebrow mb-1.5 block">Subject (optional)</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-none border-[#E8E3F0]" data-testid="paste-subject" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 block">From (optional)</Label>
            <Input value={from_} onChange={(e) => setFrom(e.target.value)} placeholder="sender@example.com" className="rounded-none border-[#E8E3F0]" data-testid="paste-from" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 block">Email body *</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} className="rounded-none border-[#E8E3F0] font-mono text-[13px]" data-testid="paste-text" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E8E3F0] flex justify-end gap-3 bg-[#FAFAF8]">
          <Button variant="outline" onClick={onClose} className="rounded-none border-[#E8E3F0]">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-none bg-[#2A0F5A] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="paste-submit">
            {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Adding…</> : "Add to inbox"}
          </Button>
        </div>
      </div>
    </div>
  );
}
